<section class="mod-page rp-page" data-module="resources">
  <header class="page-header">
    <div>
      <p class="eyebrow">Administration</p>
      <h1>Resources</h1>
      <p class="muted lead">
        Catalog modules for the
        <a routerLink="/roles">Roles &amp; permissions</a>
        matrix. Creating a resource auto-adds
        <code>_ACCESS</code> / <code>_READ</code> / <code>_WRITE</code> permission codes.
      </p>
    </div>
    @if (canManage()) {
      <div class="btn-row">
        <button type="button" class="btn" (click)="reload()" [disabled]="loading() || busy()">
          Refresh
        </button>
        <button type="button" class="btn primary" (click)="openCreate()" [disabled]="loading()">
          + Add resource
        </button>
      </div>
    }
  </header>

  @if (message()) {
    <p class="toast">{{ message() }}</p>
  }

  @if (loading()) {
    <p class="muted">Loading resources…</p>
  } @else if (!canManage()) {
    <p class="toast">Only Admin can manage RBAC resources.</p>
  } @else {
    <section class="rp-matrix-card">
      <div class="rp-matrix-head">
        <div>
          <h3>Resource catalog</h3>
          <p class="muted">{{ items().length }} live resources</p>
        </div>
      </div>

      <div class="rp-table-wrap">
        <table class="rp-matrix res-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Description</th>
              <th>Permissions</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (row of items(); track row.id) {
              <tr>
                <td>
                  <strong>{{ row.label || row.resourceName }}</strong>
                </td>
                <td>
                  <span class="code">{{ row.resourceName }}</span>
                </td>
                <td class="res-desc">{{ row.description || '—' }}</td>
                <td>
                  <span class="muted res-codes" [title]="codesPreview(row)">
                    {{ row.permissionCount || 0 }} codes
                  </span>
                </td>
                <td class="res-actions">
                  <button type="button" class="btn" (click)="openEdit(row)" [disabled]="busy()">
                    Edit
                  </button>
                  <button
                    type="button"
                    class="btn"
                    (click)="openDelete(row)"
                    [disabled]="busy() || row.canDelete === false"
                    [title]="
                      row.canDelete === false
                        ? 'Revoke desk-role grants before deleting'
                        : 'Delete resource'
                    "
                  >
                    Delete
                  </button>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="5" class="muted">No resources in the catalog.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }
</section>

@if (createOpen()) {
  <div class="admin-overlay" (click)="closeCreate()">
    <div class="admin-modal" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">
      <div class="admin-modal-head">
        <h3>Add resource</h3>
        <button type="button" class="admin-modal-close" (click)="closeCreate()" aria-label="Close">
          ×
        </button>
      </div>
      <div class="admin-modal-body">
        <div class="form-grid">
          <label>
            Display name
            <input class="field" [(ngModel)]="formLabel" name="resLabel" placeholder="Field audit" />
          </label>
          <label>
            Resource code
            <input
              class="field"
              [(ngModel)]="formCode"
              name="resCode"
              placeholder="FIELD_AUDIT"
              autocomplete="off"
            />
          </label>
          <label>
            Description
            <textarea
              class="field"
              rows="3"
              [(ngModel)]="formDescription"
              name="resDesc"
              placeholder="What this resource covers"
            ></textarea>
          </label>
        </div>
        <p class="hint muted">
          On save, the catalog gains
          <code>{{ previewAccessCode() }}</code>,
          <code>_READ</code>, and <code>_WRITE</code>.
        </p>
      </div>
      <div class="admin-modal-foot">
        <button type="button" class="btn" (click)="closeCreate()" [disabled]="busy()">Cancel</button>
        <button type="button" class="btn primary" (click)="create()" [disabled]="busy()">
          {{ busy() ? 'Creating…' : 'Create resource' }}
        </button>
      </div>
    </div>
  </div>
}

@if (editOpen() && editing(); as row) {
  <div class="admin-overlay" (click)="closeEdit()">
    <div class="admin-modal" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">
      <div class="admin-modal-head">
        <h3>Edit resource</h3>
        <button type="button" class="admin-modal-close" (click)="closeEdit()" aria-label="Close">
          ×
        </button>
      </div>
      <div class="admin-modal-body">
        <div class="form-grid">
          <label>
            Display name
            <input class="field" [(ngModel)]="formLabel" name="editLabel" />
          </label>
          <label>
            Resource code
            <input class="field" [ngModel]="formCode" name="editCode" disabled />
          </label>
          <label>
            Description
            <textarea class="field" rows="3" [(ngModel)]="formDescription" name="editDesc"></textarea>
          </label>
        </div>
        <p class="hint muted">Code is fixed because permission codes are derived from it.</p>
      </div>
      <div class="admin-modal-foot">
        <button type="button" class="btn" (click)="closeEdit()" [disabled]="busy()">Cancel</button>
        <button type="button" class="btn primary" (click)="saveEdit()" [disabled]="busy()">
          {{ busy() ? 'Saving…' : 'Save changes' }}
        </button>
      </div>
    </div>
  </div>
}

@if (deleteOpen() && deleting(); as row) {
  <div class="admin-overlay" (click)="closeDelete()">
    <div class="admin-modal" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">
      <div class="admin-modal-head">
        <h3>Delete resource?</h3>
        <button type="button" class="admin-modal-close" (click)="closeDelete()" aria-label="Close">
          ×
        </button>
      </div>
      <div class="admin-modal-body">
        <p>
          Soft-delete <strong>{{ row.label || row.resourceName }}</strong>
          (<span class="code"> ({{ row.resourceName }})</span> and its Access / Read / Write codes?
        </p>
        @if (row.canDelete === false) {
          <p class="toast">
            Still granted to: {{ (row.grantedRoleNames || []).join(', ') || 'desk roles' }}. Revoke
            those on Roles &amp; permissions first.
          </p>
        } @else {
          <p class="hint muted">Admin-only grants are cleared automatically. This cannot be undone from the UI.</p>
        }
      </div>
      <div class="admin-modal-foot">
        <button type="button" class="btn" (click)="closeDelete()" [disabled]="busy()">Cancel</button>
        <button
          type="button"
          class="btn primary"
          (click)="confirmDelete()"
          [disabled]="busy() || row.canDelete === false"
        >
          {{ busy() ? 'Deleting…' : 'Delete' }}
        </button>
      </div>
    </div>
  </div>
}
